'use client';

import React from 'react';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { PulseButton, Tooltip } from 'src/components';
import type { UserTableSort } from '../../types';

interface ColumnHeaderProps {
  label: string;
  /**
   * Explains what the column means; omit for self-evident columns. Accepts a
   * node so multi-line legends can break where they read best.
   */
  tooltip?: React.ReactNode;
  /**
   * Adds an info icon beside the label and makes *it* the tooltip's trigger, so
   * the column advertises that it has an explanation (MKT-526).
   *
   * Opt-in rather than automatic: a narrow column has no room for it — the
   * Status column is 48px and centred, and an icon there would both crowd the
   * label and break the centring.
   */
  showTooltipIcon?: boolean;
  /** API sort field. Omit to render a static, non-sortable header. */
  sortField?: string;
  activeSort?: UserTableSort;
  onSort?: (field: string) => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * @description Table header cell for the user table. Sortable columns render a
 *              real button so they are keyboard reachable, and the cell carries
 *              `aria-sort` so assistive tech announces the current ordering.
 * @returns React.ReactElement
 */
const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  label,
  tooltip,
  showTooltipIcon = false,
  sortField,
  activeSort,
  onSort,
  className = '',
  'data-testid': dataTestId,
}) => {
  const isSortable = Boolean(sortField && onSort);
  const isActive = isSortable && activeSort?.sort === sortField;
  const isAscending = activeSort?.order === 'asc';

  const ariaSort = isActive
    ? isAscending
      ? 'ascending'
      : 'descending'
    : undefined;

  const content = isSortable ? (
    <PulseButton
      size="xs"
      variant="plain"
      color="neutral"
      onClick={() => onSort?.(sortField as string)}
      endDecorator={
        isActive ? (isAscending ? faArrowUp : faArrowDown) : undefined
      }
      className="min-h-0 gap-1 whitespace-nowrap px-0 py-0 text-xs font-semibold text-neutral-700"
      aria-label={`Sort by ${label}, currently ${
        isActive ? (isAscending ? 'ascending' : 'descending') : 'unsorted'
      }`}
      data-testid={dataTestId ? `${dataTestId}-sort-button` : undefined}
    >
      {label}
    </PulseButton>
  ) : (
    <span className="block truncate">{label}</span>
  );

  /** Only a string tooltip has something an icon can announce to a reader. */
  const tooltipLabel = typeof tooltip === 'string' ? tooltip : undefined;

  // Tooltip sizes itself to its content, so cap the width and let it wrap —
  // otherwise a long explanation runs off the edge of the screen.
  const tooltipWrapper = (trigger: React.ReactElement) => (
    <Tooltip
      content={tooltip}
      delay={200}
      slotProps={{
        contentClassName: 'max-w-xs whitespace-normal text-left',
      }}
    >
      {trigger}
    </Tooltip>
  );

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={twMerge(
        'bg-neutral-50 px-2 py-3 text-left text-xs font-semibold text-neutral-700 whitespace-nowrap',
        className
      )}
      data-testid={dataTestId}
    >
      {tooltip ? (
        showTooltipIcon ? (
          // The icon alone is the trigger, so the bubble centres over it rather
          // than over the header text beside it.
          <div className="flex w-fit items-center gap-1.5">
            {content}
            {tooltipWrapper(
              <span
                className="inline-flex"
                // A node tooltip — the Status legend — has no string form to
                // announce, so the icon stays decorative rather than carrying a
                // broken label.
                role={tooltipLabel ? 'img' : undefined}
                aria-label={tooltipLabel}
                aria-hidden={tooltipLabel ? undefined : true}
                data-testid={dataTestId ? `${dataTestId}-info-icon` : undefined}
              >
                <Icon
                  icon={faCircleInfo}
                  className="text-xs text-neutral-400 hover:text-neutral-600"
                  aria-hidden="true"
                />
              </span>
            )}
          </div>
        ) : (
          tooltipWrapper(<span className="w-fit">{content}</span>)
        )
      ) : (
        content
      )}
    </th>
  );
};

export default ColumnHeader;
